import React, { useEffect, useState } from 'react';
import { LuArrowUpRight, LuArrowRight, LuMail } from 'react-icons/lu';
import {
    FaLinkedinIn, FaGithub, FaXTwitter, FaInstagram, FaFacebookF, FaMedium
} from 'react-icons/fa6';

import { clearHomeMemory } from './scrollMemory';
import { SOCIALS } from './Content_data';

import './navbar.scss';

const LINKS = [
    { id: 'about', label: 'About' },
    { id: 'expertise', label: 'Expertise' },
    { id: 'experience', label: 'Experience' },
    { id: 'myWork', label: 'Work' },
    { id: 'publications', label: 'Publications' },
];

const BLOG_URL = 'https://medium.com/@isuruariyarathna2k00';

const SOCIAL_ICONS = {
    LinkedIn: <FaLinkedinIn />,
    Github: <FaGithub />,
    Twitter: <FaXTwitter />,
    Medium: <FaMedium />,
    Instagram: <FaInstagram />,
    Facebook: <FaFacebookF />,
};

const Navigator = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeId, setActiveId] = useState('');
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const onScroll = () => {
            const scrolled = window.scrollY;
            const height = document.body.scrollHeight - window.innerHeight;
            setIsScrolled(scrolled > 40);
            setProgress(height > 0 ? (scrolled / height) * 100 : 0);
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const sections = LINKS
            .map(link => document.getElementById(link.id))
            .filter(Boolean);

        if (!sections.length) return;

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActiveId(entry.target.id);
            });
        }, { rootMargin: '-45% 0px -50% 0px' });

        sections.forEach(section => observer.observe(section));
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    useEffect(() => {
        if (!isMenuOpen) return;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') setIsMenuOpen(false);
        };

        const onResize = () => {
            if (window.innerWidth > 940) setIsMenuOpen(false);
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', onResize);
        };
    }, [isMenuOpen]);

    const goHomeSection = () => {
        clearHomeMemory();
        setIsMenuOpen(false);
    };

    const brand = (
        <a className='brand' href='/#hero' onClick={goHomeSection}>
            Isuru<span className='dot'>.</span>
        </a>
    );

    return (
        <>
            <div className='scroll-progress'>
                <span style={{ transform: `scaleX(${progress / 100})` }} />
            </div>

            <header className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
                <nav className='navbar-inner'>
                    {brand}

                    <div className='nav-links'>
                        {LINKS.map(link => (
                            <a
                                key={link.id}
                                href={`/#${link.id}`}
                                className={activeId === link.id ? 'is-active' : ''}
                                onClick={goHomeSection}
                            >
                                {link.label}
                            </a>
                        ))}
                        <a href={BLOG_URL} target='_blank' rel='noopener noreferrer'>
                            Blog
                        </a>
                    </div>

                    <a className='nav-cta' href='/#getintouch' onClick={goHomeSection}>
                        Let's talk <LuArrowUpRight />
                    </a>

                    <button
                        className={`nav-toggle ${isMenuOpen ? 'is-open' : ''}`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label='Toggle navigation menu'
                        aria-expanded={isMenuOpen}
                    >
                        <span></span>
                        <span></span>
                    </button>
                </nav>
            </header>

            <div className={`mobile-menu ${isMenuOpen ? 'is-open' : ''}`} aria-hidden={!isMenuOpen}>
                <span className='mobile-glow'></span>

                <div className='mobile-scroll'>
                    <span className='mobile-label'>Menu</span>

                    <div className='mobile-links'>
                        {LINKS.map((link, index) => (
                            <a
                                key={link.id}
                                href={`/#${link.id}`}
                                className={activeId === link.id ? 'is-active' : ''}
                                style={{ transitionDelay: `${60 + index * 45}ms` }}
                                onClick={goHomeSection}
                            >
                                <span className='index'>0{index + 1}</span>
                                <span className='label'>{link.label}</span>
                                <LuArrowRight className='arrow' />
                            </a>
                        ))}
                        <a
                            href={BLOG_URL}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ transitionDelay: `${60 + LINKS.length * 45}ms` }}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <span className='index'>0{LINKS.length + 1}</span>
                            <span className='label'>Blog</span>
                            <LuArrowUpRight className='arrow' />
                        </a>
                    </div>

                    <div className='mobile-foot'>
                        <a className='mobile-cta' href='/#getintouch' onClick={goHomeSection}>
                            Let's talk <LuArrowUpRight />
                        </a>

                        <a className='mobile-mail' href='mailto:isuruariyarathna2k00@gmail.com'>
                            <LuMail /> isuruariyarathna2k00@gmail.com
                        </a>

                        <div className='mobile-socials'>
                            {SOCIALS.map(social => (
                                <a
                                    key={social.name}
                                    href={social.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    aria-label={social.name}
                                >
                                    {SOCIAL_ICONS[social.name]}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navigator;
